import { createContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { baseURL } from "../../API/API";
import { refreshAccessToken, clearAuthStorage } from "../../utils/authRefresh";

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("accessToken");
    const user = localStorage.getItem("user");
    return token
      ? {
          accessToken: token,
          user: user ? JSON.parse(user) : null,
        }
      : {};
  });

  const bootstrapDoneRef = useRef(false);

  useEffect(() => {
    if (auth?.accessToken) {
      localStorage.setItem("accessToken", auth.accessToken);
      if (auth?.user) {
        localStorage.setItem("user", JSON.stringify(auth.user));
      }
    } else {
      clearAuthStorage();
    }
  }, [auth]);

  useEffect(() => {
    const bootstrapAuth = async () => {
      if (bootstrapDoneRef.current) return;
      bootstrapDoneRef.current = true;

      const storedToken = localStorage.getItem("accessToken");
      if (!storedToken) return;

      const applyUser = (userData, accessToken) => {
        setAuth((prev) => ({
          ...prev,
          accessToken: accessToken || prev.accessToken,
          user: userData || prev.user,
        }));
      };

      try {
        const meRes = await axios.get(`${baseURL}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        const userData = meRes.data?.data ?? meRes.data;
        applyUser(userData, storedToken);
        return;
      } catch (meError) {
        if (meError?.response?.status !== 401) {
          return;
        }
      }

      try {
        const newAccessToken = await refreshAccessToken();
        const meRes = await axios.get(`${baseURL}/auth/me`, {
          headers: { Authorization: `Bearer ${newAccessToken}` },
        });
        const userData = meRes.data?.data ?? meRes.data;
        applyUser(userData, newAccessToken);
      } catch {
        const hasRefresh = !!localStorage.getItem("refreshToken");
        if (!hasRefresh) {
          setAuth({});
        }
      }
    };

    bootstrapAuth();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!auth?.accessToken || auth?.user) return;

      const loadMe = async (token) => {
        const response = await axios.get(`${baseURL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data?.data ?? response.data;
      };

      try {
        const userData = await loadMe(auth.accessToken);
        if (userData) {
          setAuth((prev) => ({ ...prev, user: userData }));
        }
      } catch (error) {
        if (error?.response?.status !== 401) {
          console.error("خطأ في جلب بيانات المستخدم:", error);
          return;
        }

        try {
          const newAccessToken = await refreshAccessToken();
          const userData = await loadMe(newAccessToken);
          if (userData) {
            setAuth((prev) => ({
              ...prev,
              accessToken: newAccessToken,
              user: userData,
            }));
          }
        } catch (refreshError) {
          if (refreshError?.response?.status === 401) {
            setAuth({});
          }
        }
      }
    };

    fetchUserData();
  }, [auth?.accessToken, auth?.user]);

  return (
    <AuthContext.Provider value={{ auth, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
